import classNames from 'classnames';
import { useState } from 'react';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { DialogType } from '../../common/enums';
import {
  editSettingsMsg,
  instructionsForServiceMsg,
  instructionsMsg,
  seeMsg,
  t,
  vakioAdditionalSettingsMsg,
  vakioServiceSettings,
} from '../../common/i18n';
import { isFeatureEnabled } from '../../common/utils/featureFlagUtils';
import { getAddonAssociationsAndDependencies, getConfiguredPbxSolution } from '../../common/utils/subscriptionUtils';
import { SubscriptionStatusType } from '../../generated/api/subscriptionStatusType';
import { useFeatureFlags } from '../../public/site/ConfigProvider';
import { openDialog } from '../../public/site/dialogStore.js';
import { DetailsWithCard } from '../DetailsWithInfoColumn/DetailsWithInfoColumn';
import { HeaderWithEditButton } from '../HeaderWithButton/HeaderWithButton';
import { PbxAccordionContent } from '../PbxAccordionContent/PbxAccordionContent';
import { ReachabilityWithMobileAccordion } from '../ReachabilityWithMobile/ReachabilityWithMobileAccordion';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse';
import type { Subscription } from '../../generated/api/subscription';

export interface VoiceSubscriptionVakioSettingsProps {
  subscription: Subscription;
  hasPendingActions: boolean;
  companyInfo?: CompanyInfoResponse;
  addOnRules?: AddOnRulesResponse;
  addOnRulesMobilePbx?: AddOnRulesResponse;
}

export const VoiceSubscriptionVakioSettings = ({
  subscription,
  hasPendingActions,
  companyInfo,
  addOnRules,
  addOnRulesMobilePbx,
}: VoiceSubscriptionVakioSettingsProps) => {
  const featureFlags = useFeatureFlags();

  const [isEditingVakioDetails, setEditingVakioDetails] = useState(false);
  const [isEditingReachability, setEditingReachability] = useState(false);
  const { associations, dependencies } = getAddonAssociationsAndDependencies(
    subscription,
    addOnRules,
    addOnRulesMobilePbx
  );
  const configuredPbxSolution = getConfiguredPbxSolution(subscription, companyInfo?.pbxSolutions ?? [])!;

  const onEdit = () => {
    if (subscription?.subscriptionStatus === SubscriptionStatusType.IN_ACTIVATION) {
      openDialog({ type: DialogType.MACD_FORBIDDEN_SUBSCRIPTION_IN_ACTIVATION });
      return;
    }
    if (hasPendingActions) {
      openDialog({ type: DialogType.SUBSCRIPTION_ACTION_PENDING });
      return;
    }
    setEditingVakioDetails(true);
  };
  return (
    <DetailsWithCard
      cardContent={
        <>
          <h3>{t.RD4R(instructionsMsg)}</h3>
          <p>
            {t.LJ10(seeMsg)}
            <Link to={paths.INSTRUCTIONS_VAKIO} target="_blank" rel="noopener noreferrer">
              {t.Z72R(instructionsMsg.toLowerCase())}
            </Link>
            {t.U63I(instructionsForServiceMsg)}
          </p>
        </>
      }
    >
      <HeaderWithEditButton
        title={t.ZCXQ(vakioServiceSettings)}
        showButton={!isEditingVakioDetails}
        buttonText={t.YBJF(editSettingsMsg)}
        onClick={() => onEdit()}
      />
      <PbxAccordionContent
        subscription={subscription}
        hasPendingActions={hasPendingActions}
        pbxSolution={configuredPbxSolution}
        isEditing={isEditingVakioDetails}
        setEditing={setEditingVakioDetails}
        hideEditButton={true}
      />
      {isFeatureEnabled(featureFlags.showReachabilityWithMobile) && (
        <>
          <div className={classNames('section-separator', dsClass.MARGIN_TOP_8)} />
          <h3 className={classNames(dsClass.H3_BOOK, dsClass.MARGIN_BOTTOM_6)}>{t.WJIG(vakioAdditionalSettingsMsg)}</h3>
          <ReachabilityWithMobileAccordion
            subscription={subscription}
            hasPendingActions={hasPendingActions}
            pbxSolution={configuredPbxSolution}
            addOnRulesAssociations={associations}
            addOnRulesDependencies={dependencies}
            isEditing={isEditingReachability}
            setEditing={setEditingReachability}
            whiteButton={true}
          />
        </>
      )}
    </DetailsWithCard>
  );
};
